import { Component, computed, effect, inject, signal, output, DestroyRef, Injector, runInInjectionContext } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { GenericFormService } from './generic-form-service';
import { NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { GenericField } from './generic-field/generic-field';
import { GenericFormField } from './model/generic-field';
import { GenericValidator } from './model/generic-validator';
import { getRequiredFromDependencies } from './utils/field-dependency-utils';
import { ValidationTypes } from './enums/validation-types';
import { CommonModule } from '@angular/common';
import { ZnButton } from '../zn-button/zn-button';
import { LocalizationService } from '@abp/ng.core';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService, Confirmation, ModalComponent } from '@abp/ng.theme.shared';
import { localizeWithFallback } from '../utils/localization-utils';
import { ZendraLocalizationPipe } from '../pipes/zendra-localization.pipe';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-generic-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GenericField,
    NgbDatepickerModule,
    ZnButton,
    ZendraLocalizationPipe,
    ModalComponent,
  ],
  providers: [GenericFormService],
  templateUrl: './generic-form.html',
  styleUrl: './generic-form.scss',
})

/**
 * Self-contained generic form with modal.
 * Use the open() method to display the form with specified fields.
 *
 * Example:
 * @ViewChild(GenericForm) genericForm!: GenericForm;
 *
 * this.genericForm.open({
 *   title: 'Edit User',
 *   fields: [...],
 *   entitylocaleKey: 'General::Entity.UserService'
 * });
 */
export class GenericForm {
  private readonly geneticFormService = inject(GenericFormService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly localizationService = inject(LocalizationService);
  private readonly injector = inject(Injector);

  // Internal state
  fields = signal<GenericFormField[]>([]);
  cssClasses = signal<string | string[]>([]);
  mode = signal<'create' | 'edit'>('create');
  originalValues = signal<Record<string, any>>({});
  entityLocaleKey = signal<string | ZendraLocalizationServices>(
    ZendraLocalizationServices.LanguageService
  );
  private formState: any = null;
  private dependencySubscription?: Subscription;
  private isApplyingDependencies = false;
  private baseRequired = new Map<string, boolean>();
  private baseValidators = new Map<string, GenericValidator[]>();
  private requiredValidators = new Map<string, GenericValidator>();

  isVisible = signal(false);
  modalTitle = signal('');
  localizationServices = ZendraLocalizationServices;
  visibleFields = computed(() => this.fields().filter(field => !field.hidden));

  // Outputs
  payload = output<any>();
  formCancel = output();

  form = signal<FormGroup>(new FormGroup({}));

  constructor() {
    effect(() => {
      const fields = this.fields();

      if (!fields) return;

      fields.forEach(field => {
        if (field.optionsLoader && field.options.length === 0) {
          runInInjectionContext(this.injector, () => field.optionsLoader!())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (response) => {
                const options = Array.isArray(response) ? response : response.items;
                field.options = options;
              },
              error: (error) => {
                console.error(`Error loading options for field ${field.key}:`, error);
              }
            });
        }
      });
    });
  }

  open(config: {
    title: string;
    fields: GenericFormField[];
    entityLocaleKey?: string | ZendraLocalizationServices;
    cssClasses?: string | string[];
    mode?: 'create' | 'edit';
  }): void {
    this.mode.set(config.mode ?? 'create');
    this.modalTitle.set(config.title);
    this.fields.set(config.fields);
    this.form.set(this.geneticFormService.toFormGroup(config.fields));
    this.originalValues.set(
      Object.fromEntries(config.fields.map(field => [field.key, field.value]))
    );
    this.setupDependencies();
    this.entityLocaleKey.set(config.entityLocaleKey || ZendraLocalizationServices.LanguageService);
    this.cssClasses.set(config.cssClasses || []);
    this.isVisible.set(true);
  }

  close(): void {
    this.isVisible.set(false);
    this.teardownDependencies();
    this.form().reset();
    this.originalValues.set({});
    this.mode.set('create');
  }

  onSubmit() {
    const formValue = this.form().getRawValue();
    const nonEmptyObj = Object.fromEntries(Object.entries(formValue).filter(([_, value]) => value !== ''));
    this.payload.emit(nonEmptyObj);
    this.close();
  }

  onFormCancel() {
    this.handleCancelWithConfirmation();
  }

  onModalVisibleChange(visible: boolean) {
    if (!visible && this.isVisible()) {
      this.handleCancelWithConfirmation();
    }
  }

  private handleCancelWithConfirmation(): void {
    if (!this.form().dirty) {
      this.closeAndNotify();
      return;
    }

    // Save form state before closing
    this.formState = this.form().getRawValue();

    this.isVisible.set(false);
    this.showCancelConfirmation().subscribe((status) => {
      if (status === Confirmation.Status.confirm) {
        this.closeAndNotify();
      } else {
        this.reopenModal();
      }
    });
  }

  private showCancelConfirmation() {

    return this.confirmationService.show(
      localizeWithFallback(this.localizationService, 'LanguageService::ConfirmCancelChanges'),
      localizeWithFallback(this.localizationService, 'LanguageService::ConfirmCancelChangesTitle'),
      'warning',
      {
        iconTemplate: '<i class="bi bi-exclamation-octagon-fill"></i>',
        yesText: localizeWithFallback(this.localizationService, 'LanguageService::Option:Yes'),
        cancelText: localizeWithFallback(this.localizationService, 'LanguageService::Option:No')
      }
    );
  }

  /**
   * Closes the modal and emits the cancel event
   */
  private closeAndNotify(): void {
    this.isVisible.set(false);
    this.teardownDependencies();
    this.form().reset();
    this.originalValues.set({});
    this.mode.set('create');
    this.formState = null;
    this.formCancel.emit();
  }

  private reopenModal(): void {
    // Update fields with current form values before reopening
    if (this.formState) {
      const currentFields = this.fields();
      const updatedFields = currentFields.map(field => ({
        ...field,
        value: this.formState[field.key] ?? field.value
      }));
      this.fields.set(updatedFields);
      this.form().reset(this.formState, { emitEvent: false });
      this.applyDependencies();
    }

    this.isVisible.set(false);
    setTimeout(() => {
      this.isVisible.set(true);

      // Mark form as dirty after reopening to preserve the modified state
      setTimeout(() => {
        this.form().markAsDirty();
        this.formState = null;
      }, 0);
    }, 0);
  }

  private setupDependencies(): void {
    this.teardownDependencies();
    const fields = this.fields();
    if (!fields || fields.length === 0) return;

    this.initializeDependencyState(fields);

    const hasDependencies = fields.some(field => (field.dependencies ?? []).length > 0);
    if (!hasDependencies) return;

    this.dependencySubscription = this.form().valueChanges.subscribe(() => {
      if (this.isApplyingDependencies) return;
      this.applyDependencies();
    });

    this.applyDependencies();
  }

  private teardownDependencies(): void {
    if (this.dependencySubscription) {
      this.dependencySubscription.unsubscribe();
      this.dependencySubscription = undefined;
    }
    this.isApplyingDependencies = false;
    this.baseRequired.clear();
    this.baseValidators.clear();
    this.requiredValidators.clear();
  }

  private initializeDependencyState(fields: GenericFormField[]): void {
    this.baseRequired.clear();
    this.baseValidators.clear();
    this.requiredValidators.clear();

    for (const field of fields) {
      this.baseRequired.set(field.key, !!field.required);

      const validators = field.validators ?? [];
      const requiredValidator =
        validators.find(v => v.key === ValidationTypes.RequiredTrue) ??
        validators.find(v => v.key === ValidationTypes.Required);
      const nonRequiredValidators = validators.filter(
        v => v.key !== ValidationTypes.Required && v.key !== ValidationTypes.RequiredTrue
      );

      this.baseValidators.set(field.key, nonRequiredValidators);
      if (requiredValidator) {
        this.requiredValidators.set(field.key, requiredValidator);
      } else {
        this.requiredValidators.set(
          field.key,
          new GenericValidator({ key: ValidationTypes.Required })
        );
      }
    }
  }

  private applyDependencies(): void {
    const fields = this.fields();
    if (!fields || fields.length === 0) return;

    const values = this.form().getRawValue();
    let shouldRefreshFields = false;
    let updatedFields = fields;

    this.isApplyingDependencies = true;
    try {
      updatedFields = fields.map(field => {
        const rules = field.dependencies ?? [];
        if (rules.length === 0) {
          return field;
        }

        const baseRequired = this.baseRequired.get(field.key) ?? field.required;
        const required = getRequiredFromDependencies(rules, values, baseRequired);

        let nextValidators = field.validators;
        if (field.required !== required) {
          nextValidators = this.getValidatorsForRequired(field.key, required);
          this.updateControlValidators(field, required, nextValidators);
          shouldRefreshFields = true;
        }

        if (field.required === required) {
          return field;
        }

        return {
          ...field,
          required,
          validators: nextValidators,
          value: field.value,
        };
      });
    } finally {
      this.isApplyingDependencies = false;
    }

    if (shouldRefreshFields) {
      this.fields.set(updatedFields);
    }
  }

  private updateControlValidators(
    field: GenericFormField,
    required: boolean,
    validatorsOverride?: GenericValidator[]
  ): void {
    const control = this.form().controls[field.key];
    if (!control) return;

    const validatorsToApply = validatorsOverride ?? this.getValidatorsForRequired(field.key, required);
    const angularValidators = this.geneticFormService.generateValidators(validatorsToApply);

    control.setValidators(angularValidators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private getValidatorsForRequired(fieldKey: string, required: boolean): GenericValidator[] {
    const baseValidators = this.baseValidators.get(fieldKey) ?? [];
    const requiredValidator = this.requiredValidators.get(fieldKey);
    return required
      ? [...baseValidators, ...(requiredValidator ? [requiredValidator] : [])]
      : [...baseValidators];
  }

  // dependency evaluation moved to shared utils
}
